// ./src/components/features/CreateEventWizard/scheduleApply.ts
//
// Save a repeating event's rule and create its dates (ROADMAP A2b #62 P6 — the wizard's Schedule step). Shared by the
// step's "Create the dates now", the save on Next and Finish from any step, so they cannot write the rule differently.
//
// ⚠⚠ `settings` IS ONE jsonb COLUMN AND AN UPDATE REPLACES IT — so the event is RE-READ and the rule merged in, never
//   written over a copy the step loaded minutes ago (features, the hero, the questions' settings all live there too).
//   ⚠ A read that comes back with NO row writes nothing (it would replace every setting with the rule alone).
// ⚠ Whether dates exist is READ, never remembered (review, 2026-09-30): a rule saved while its dates failed, or dates
//   removed since, must not pass for "done".
import { OP_RESULT_CODES, OpResult } from '@sdflc/api-helpers';

import { Event, EventService, EventTimeSlotService, RecurrenceRule } from '@/utils/api';
import { defaultRecurrenceRule } from '@/pages/private/EventSchedulePage/recurrenceDefaults';

/** The event as it is now, or the refusal to write anything. */
const readEvent = async (eventId: string): Promise<Event | OpResult> => {
  const current = await EventService.get(eventId);

  if (!current.didSucceed()) {
    return current;
  }

  const event = current.getDataFirst() as Event | undefined;

  if (!event?.id) {
    return new OpResult({ code: OP_RESULT_CODES.NOT_FOUND }).addError('', 'The event could not be read. Please try again.');
  }

  if (!event.startsAt) {
    // ⚠ A rule anchors on the start: without one the server makes NO dates and still reports success.
    return new OpResult({ code: OP_RESULT_CODES.VALIDATION_FAILED }).addError('', 'Set when the event starts (on Basics) before its dates can be created.');
  }

  return event;
};

const saveAndGenerate = async (event: Event, rule: RecurrenceRule): Promise<OpResult> => {
  const saved = await EventService.update({ id: event.id as string }, { settings: { ...(event.settings ?? {}), recurrence: rule } });

  if (!saved.didSucceed()) {
    return saved;
  }

  return EventTimeSlotService.generate(event.id as string);
};

/** "Create the dates now": save this rule and create its dates. */
export const saveRecurrenceAndGenerate = async (eventId: string, rule: RecurrenceRule): Promise<OpResult> => {
  const event = await readEvent(eventId);

  return event instanceof OpResult ? event : saveAndGenerate(event, rule);
};

export interface EnsureDatesResult {
  result: OpResult;
  /** The rule now in force (the step's, the saved one, or the default), or null when nothing could be read. */
  rule: RecurrenceRule | null;
}

/**
 * Leaving the Schedule step, or finishing from ANY step: make sure a repeating event HAS dates. The rule is the step's
 * (`rule`), else the one saved on the event, else the default from its start and end — a host who never opened the
 * step (Finish on Basics after switching to "Repeats") still gets dates. It is written and the dates made when the rule
 * CHANGED on the step, or when the event has no dates at all; otherwise nothing is written.
 */
export const ensureRecurringDates = async (eventId: string, rule: RecurrenceRule | null, ruleChanged: boolean): Promise<EnsureDatesResult> => {
  const event = await readEvent(eventId);

  if (event instanceof OpResult) {
    return { result: event, rule: null };
  }

  const tz = event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const effective = rule ?? event.settings?.recurrence ?? defaultRecurrenceRule(event.startsAt, tz, event.endsAt);
  const slots = await EventTimeSlotService.list({ eventId: [event.id as string] });

  if (!slots.didSucceed()) {
    return { result: slots, rule: effective };
  }

  const hasDates = Array.isArray(slots.data) && slots.data.length > 0;

  if (!ruleChanged && hasDates) {
    return { result: slots, rule: effective };
  }

  return { result: await saveAndGenerate(event, effective), rule: effective };
};
